import React from "react";
import { NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Users, Inbox, ClipboardCheck, ShieldCheck, KeyRound, CheckCircle2,
  Briefcase, UsersRound, Receipt, GitBranch, ScrollText, Settings2, LogOut, Building2,
  Wallet, Target, Server, PhoneCall, LineChart, Home,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FullPageLoader } from "@/components/ds";

// Sections available now (Phase 1 + Module 39 Sales). Others are placeholders shipping in later phases.
const NAV = [
  { to: "/internal", label: "Overview", icon: LayoutDashboard, end: true, ready: true },
  { to: "/internal/onboarding-accounts", label: "New Onboarding", icon: KeyRound, ready: true },
  { to: "/internal/customers", label: "Customers", icon: Users, ready: false },
  { to: "/internal/awaiting-setup", label: "Awaiting Setup", icon: Inbox, ready: false },
  { to: "/internal/onboarding", label: "Onboarding", icon: ClipboardCheck, ready: false },
  { to: "/internal/qa", label: "Ready for QA", icon: ShieldCheck, ready: false },
  { to: "/internal/access", label: "Ready for Access", icon: KeyRound, ready: false },
  { to: "/internal/active", label: "Active Customers", icon: CheckCircle2, ready: false },
  { to: "/internal/sales", label: "Sales Dashboard", icon: Briefcase, ready: true, masterOnly: true, end: true },
  { to: "/internal/sales/reps", label: "Representatives", icon: UsersRound, ready: true, masterOnly: true },
  { to: "/internal/sales/ledger", label: "Sales Ledger", icon: Receipt, ready: true, masterOnly: true },
  { to: "/internal/sales/payouts", label: "Payouts", icon: Wallet, ready: true, masterOnly: true },
  { to: "/internal/sales/growth-mix", label: "Growth Mix", icon: GitBranch, ready: true, masterOnly: true },
  { to: "/internal/sales/me", label: "My Performance", icon: Target, ready: true, repOnly: true },
  { to: "/executive", label: "Executive Command Center", icon: LineChart, ready: true, commandCenter: true },
  { to: "/internal/infrastructure", label: "Platform Infrastructure", icon: Server, ready: true, masterOnly: true },
  { to: "/internal/telephony", label: "Telephony", icon: PhoneCall, ready: true, masterOnly: true },
  { to: "/internal/audit-logs", label: "Audit Logs", icon: ScrollText, ready: true, masterOnly: true },
  { to: "/internal/settings", label: "Platform Settings", icon: Settings2, ready: false, masterOnly: true },
];

export default function InternalLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data, isLoading, isError } = useQuery({ queryKey: ["internal-me"], queryFn: api.internalMe, retry: false });

  if (user === null || isLoading) return <FullPageLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (isError || !data) return <Navigate to="/app/dashboard" replace />; // not internal → bounce to customer app

  const isMaster = data.is_senior_admin;
  const isRep = data.role === "sales_rep";
  // The Command Center is served to the senior admin (all data) and to reps (own data only).
  // A plain platform_admin is refused by the API, so don't offer them the link.
  const canCommandCenter = isMaster || isRep;
  const items = NAV.filter((n) =>
    (!(n as any).masterOnly || isMaster)
    && (!(n as any).repOnly || isRep)
    && (!(n as any).commandCenter || canCommandCenter));

  // Company-wide sales and platform screens are the Master Admin's alone, and the API enforces it too.
  // Anyone else opening one by its address goes to the overview instead of a page that cannot load.
  const masterOnlyPage = NAV.some((n) => (n as any).masterOnly
    && (pathname === n.to || (!n.end && pathname.startsWith(`${n.to}/`))));
  if (masterOnlyPage && !isMaster) return <Navigate to="/internal" replace />;

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100" data-testid="internal-portal">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-slate-800 bg-slate-900 lg:flex">
        <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-4">
          <Building2 className="h-5 w-5 text-blue-400" />
          <div>
            <p className="text-sm font-semibold">ResFil Internal</p>
            <p className="text-[11px] uppercase tracking-wide text-slate-500">{data.role.replace(/_/g, " ")}</p>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
          {items.map((n) => {
            const testid = `internal-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`;
            const label = <span className="flex items-center gap-2"><n.icon className="h-4 w-4" /> {n.label}</span>;
            // Not built yet. Listed so the roadmap is visible, but not a link: these pages do not exist,
            // and following one used to drop the user on the public home page.
            if (!n.ready) {
              return (
                <div key={n.to} aria-disabled="true" data-testid={testid}
                  className="flex cursor-default items-center justify-between gap-2 rounded-md px-3 py-2 text-sm text-slate-300 opacity-50">
                  {label}
                  <span className="rounded bg-slate-700 px-1.5 text-[10px] text-slate-300">soon</span>
                </div>
              );
            }
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={testid}
                className={({ isActive }) => `flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
                {label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-800">
          {/* The Master Admin goes back to his business dashboard; other staff to the portal overview. */}
          <button onClick={() => navigate(isMaster ? "/app/dashboard" : "/internal")}
            className="flex w-full items-center gap-2 px-5 pb-1 pt-3 text-sm text-slate-400 hover:text-white" data-testid="internal-dashboard">
            <Home className="h-4 w-4" /> Dashboard
          </button>
          <button onClick={async () => { await logout(); navigate("/login"); }}
            className="flex w-full items-center gap-2 px-5 pb-3 pt-1 text-sm text-slate-400 hover:text-white" data-testid="internal-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden bg-slate-50 text-slate-900">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">{children}</div>
      </main>
    </div>
  );
}
